import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  LEAVE_REQUEST_STATUS,
  LEAVE_REQUEST_TYPE,
  LeaveRequest,
  Student,
  Teacher,
} from "../models";
import { notificationService } from "../services/notification-service";
import { formatLeaveRequestDocument } from "../utils/leave-request-response";
import { logger } from "../utils/logger";
import {
  parseStudentLeaveDayInput,
  utcCalendarDayStart,
} from "../utils/student-leave-date";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class LeaveController {
  /**
   * Create a new leave request (Student)
   * @route POST /api/v1/student/leave-requests
   * @access Private (Student)
   */
  async createLeaveRequest(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { date: leaveDateInput, reason } = req.body;
      const files = (req as any).files as Express.Multer.File[];

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (reason === undefined || reason === null) {
        res.status(400).json({
          success: false,
          message: "reason is required.",
        } as APIResponse);
        return;
      }

      const trimmedReason = String(reason).trim();
      if (trimmedReason === "") {
        res.status(400).json({
          success: false,
          message: "reason must not be empty.",
        } as APIResponse);
        return;
      }

      if (trimmedReason.length > 1000) {
        res.status(400).json({
          success: false,
          message: "Reason cannot exceed 1000 characters.",
        } as APIResponse);
        return;
      }

      const parsedDay = parseStudentLeaveDayInput(leaveDateInput);
      if (!parsedDay.ok) {
        res.status(400).json({
          success: false,
          message: parsedDay.message,
        } as APIResponse);
        return;
      }

      const leaveDay = parsedDay.utcDayStart;

      // Find student profile
      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const enrollmentOrJoin = student.enrollmentDate ?? student.createdAt;
      const joinDay = utcCalendarDayStart(enrollmentOrJoin);
      if (leaveDay < joinDay) {
        res.status(400).json({
          success: false,
          message:
            "Leave date cannot be before your enrollment (joining) date.",
        } as APIResponse);
        return;
      }

      // Process uploaded files (images)
      const documents =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      // Create leave request
      const leaveRequest = new LeaveRequest({
        studentId: student._id,
        schoolId: student.schoolId,
        leaveType: LEAVE_REQUEST_TYPE.OTHER,
        startDate: leaveDay,
        endDate: leaveDay,
        reason: trimmedReason,
        documents,
        status: LEAVE_REQUEST_STATUS.PENDING,
      });

      await leaveRequest.save();

      // Populate student info for response
      const populatedRequest = await LeaveRequest.findById(
        leaveRequest._id,
      ).populate("student", "fullName studentId");

      logger.info(`Leave request created: ${leaveRequest.id}`, {
        leaveRequestId: leaveRequest.id,
        studentId: student.id,
        schoolId: student.schoolId,
      });

      const populatedStudent = populatedRequest
        ? (populatedRequest as any).student
        : null;

      const dateIso = leaveDay.toISOString().slice(0, 10);

      res.status(201).json({
        success: true,
        message: "Leave request created successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            date: dateIso,
            leaveType: leaveRequest.leaveType,
            startDate: leaveRequest.startDate,
            endDate: leaveRequest.endDate,
            reason: leaveRequest.reason,
            status: leaveRequest.status,
            durationDays: (leaveRequest as any).durationDays,
            student: populatedStudent
              ? {
                  id: populatedStudent.id || populatedStudent._id,
                  fullName: populatedStudent.fullName,
                  studentId: populatedStudent.studentId,
                }
              : {
                  id: student.id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                },
            createdAt: leaveRequest.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create leave request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all leave requests for a student
   * @route GET /api/v1/student/leave-requests
   * @access Private (Student)
   */
  async getStudentLeaveRequests(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { status, page = 1, limit = 10 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { studentId: student._id };
      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const leaveRequests = await LeaveRequest.find(filterQuery)
        .populate("student", "fullName studentId")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await LeaveRequest.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Leave requests retrieved successfully",
        data: {
          leaveRequests: leaveRequests.map(lr => ({
            id: lr.id,
            leaveType: lr.leaveType,
            startDate: lr.startDate,
            endDate: lr.endDate,
            reason: lr.reason,
            status: lr.status,
            durationDays: (lr as any).durationDays,
            approvedBy: lr.approvedBy,
            approvedAt: lr.approvedAt,
            rejectedBy: lr.rejectedBy,
            rejectedAt: lr.rejectedAt,
            rejectionReason: lr.rejectionReason,
            documents: lr.documents,
            createdAt: lr.createdAt,
            updatedAt: lr.updatedAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get student leave requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve leave requests",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all leave requests for school panel
   * @route GET /api/v1/school/leave-requests
   * @access Private (School Admin)
   */
  async getSchoolLeaveRequests(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const {
        status,
        leaveType,
        studentId,
        startDate,
        endDate,
        page = 1,
        limit = 20,
        sortBy = "createdAt",
        sortOrder = "desc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (status) {
        filterQuery.status = status;
      }

      if (leaveType) {
        filterQuery.leaveType = leaveType;
      }

      if (studentId) {
        filterQuery.studentId = new mongoose.Types.ObjectId(
          studentId as string,
        );
      }

      if (startDate || endDate) {
        filterQuery.$or = [];
        if (startDate) {
          filterQuery.$or.push({
            startDate: { $gte: new Date(startDate as string) },
          });
        }
        if (endDate) {
          filterQuery.$or.push({
            endDate: { $lte: new Date(endDate as string) },
          });
        }
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const sort: any = {};
      sort[sortBy as string] = sortOrder === "asc" ? 1 : -1;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const leaveRequests = await LeaveRequest.find(filterQuery)
        .populate("student", "fullName studentId profilePicture")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email")
        .sort(sort)
        .skip(skip)
        .limit(limitNum);

      const total = await LeaveRequest.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Leave requests retrieved successfully",
        data: {
          leaveRequests: leaveRequests.map(lr => {
            const lrDoc = lr as any;
            const student = lrDoc.student;
            return {
              id: lr.id,
              leaveType: lr.leaveType,
              startDate: lr.startDate,
              endDate: lr.endDate,
              reason: lr.reason,
              status: lr.status,
              durationDays: lrDoc.durationDays,
              student: student
                ? {
                    id: student.id || student._id,
                    fullName: student.fullName,
                    studentId: student.studentId,
                    profilePicture: student.profilePicture,
                  }
                : null,
              approvedBy: lrDoc.approvedBy
                ? {
                    id: lrDoc.approvedBy.id || lrDoc.approvedBy._id,
                    fullName: lrDoc.approvedBy.fullName,
                    email: lrDoc.approvedBy.email,
                  }
                : null,
              approvedAt: lr.approvedAt,
              rejectedBy: lrDoc.rejectedBy
                ? {
                    id: lrDoc.rejectedBy.id || lrDoc.rejectedBy._id,
                    fullName: lrDoc.rejectedBy.fullName,
                    email: lrDoc.rejectedBy.email,
                  }
                : null,
              rejectedAt: lr.rejectedAt,
              rejectionReason: lr.rejectionReason,
              documents: lr.documents,
              createdAt: lr.createdAt,
              updatedAt: lr.updatedAt,
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school leave requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve leave requests",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get a single leave request by ID
   * @route GET /api/v1/school/leave-requests/:id
   * @access Private (School Admin)
   */
  async getLeaveRequestById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid leave request ID",
        } as APIResponse);
        return;
      }

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("student", "fullName studentId profilePicture phone")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email");

      if (!leaveRequest) {
        res.status(404).json({
          success: false,
          message: "Leave request not found",
        } as APIResponse);
        return;
      }

      const lrDoc = leaveRequest as any;
      const student = lrDoc.student;

      res.status(200).json({
        success: true,
        message: "Leave request retrieved successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            leaveType: leaveRequest.leaveType,
            startDate: leaveRequest.startDate,
            endDate: leaveRequest.endDate,
            reason: leaveRequest.reason,
            status: leaveRequest.status,
            durationDays: lrDoc.durationDays,
            student: student
              ? {
                  id: student.id || student._id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                  profilePicture: student.profilePicture,
                  phone: student.phone,
                }
              : null,
            approvedBy: lrDoc.approvedBy
              ? {
                  id: lrDoc.approvedBy.id || lrDoc.approvedBy._id,
                  fullName: lrDoc.approvedBy.fullName,
                  email: lrDoc.approvedBy.email,
                }
              : null,
            approvedAt: leaveRequest.approvedAt,
            rejectedBy: lrDoc.rejectedBy
              ? {
                  id: lrDoc.rejectedBy.id || lrDoc.rejectedBy._id,
                  fullName: lrDoc.rejectedBy.fullName,
                  email: lrDoc.rejectedBy.email,
                }
              : null,
            rejectedAt: leaveRequest.rejectedAt,
            rejectionReason: leaveRequest.rejectionReason,
            documents: leaveRequest.documents,
            createdAt: leaveRequest.createdAt,
            updatedAt: leaveRequest.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get leave request by ID error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve leave request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Approve a leave request
   * @route PUT /api/v1/school/leave-requests/:id/approve
   * @access Private (School Admin)
   */
  async approveLeaveRequest(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid leave request ID",
        } as APIResponse);
        return;
      }

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!leaveRequest) {
        res.status(404).json({
          success: false,
          message: "Leave request not found",
        } as APIResponse);
        return;
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.PENDING) {
        res.status(400).json({
          success: false,
          message: `Leave request is already ${leaveRequest.status}`,
        } as APIResponse);
        return;
      }

      await leaveRequest.approve(new mongoose.Types.ObjectId(userId), "school");

      const populatedRequest = await LeaveRequest.findById(
        leaveRequest._id,
      ).populate("student", "fullName studentId");
      const populatedStudent = populatedRequest
        ? (populatedRequest as any).student
        : null;

      // Notify student
      await notificationService.notifyLeaveRequestStatus(
        leaveRequest.studentId,
        new mongoose.Types.ObjectId(schoolId),
        true,
        leaveRequest.id,
        leaveRequest.startDate,
        leaveRequest.endDate,
      );

      logger.info(`Leave request approved: ${leaveRequest.id}`, {
        leaveRequestId: leaveRequest.id,
        approvedBy: userId,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Leave request approved successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            status: leaveRequest.status,
            approvedBy: leaveRequest.approvedBy,
            approvedAt: leaveRequest.approvedAt,
            student: populatedStudent
              ? {
                  id: populatedStudent.id || populatedStudent._id,
                  fullName: populatedStudent.fullName,
                }
              : null,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Approve leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve leave request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reject a leave request
   * @route PUT /api/v1/school/leave-requests/:id/reject
   * @access Private (School Admin)
   */
  async rejectLeaveRequest(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { rejectionReason } = req.body;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid leave request ID",
        } as APIResponse);
        return;
      }

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!leaveRequest) {
        res.status(404).json({
          success: false,
          message: "Leave request not found",
        } as APIResponse);
        return;
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.PENDING) {
        res.status(400).json({
          success: false,
          message: `Leave request is already ${leaveRequest.status}`,
        } as APIResponse);
        return;
      }

      await leaveRequest.reject(
        new mongoose.Types.ObjectId(userId),
        rejectionReason || "Leave request rejected",
        "school",
      );

      const populatedRequest = await LeaveRequest.findById(
        leaveRequest._id,
      ).populate("student", "fullName studentId");
      const populatedStudent = populatedRequest
        ? (populatedRequest as any).student
        : null;

      // Notify student
      await notificationService.notifyLeaveRequestStatus(
        leaveRequest.studentId,
        new mongoose.Types.ObjectId(schoolId),
        false,
        leaveRequest.id,
        leaveRequest.startDate,
        leaveRequest.endDate,
      );

      logger.info(`Leave request rejected: ${leaveRequest.id}`, {
        leaveRequestId: leaveRequest.id,
        rejectedBy: userId,
        schoolId,
        rejectionReason,
      });

      res.status(200).json({
        success: true,
        message: "Leave request rejected successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            status: leaveRequest.status,
            rejectedBy: leaveRequest.rejectedBy,
            rejectedAt: leaveRequest.rejectedAt,
            rejectionReason: leaveRequest.rejectionReason,
            student: populatedStudent
              ? {
                  id: populatedStudent.id || populatedStudent._id,
                  fullName: populatedStudent.fullName,
                }
              : null,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Reject leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject leave request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Cancel a leave request (Student)
   * @route PUT /api/v1/student/leave-requests/:id/cancel
   * @access Private (Student)
   */
  async cancelLeaveRequest(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid leave request ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        studentId: student._id,
      });

      if (!leaveRequest) {
        res.status(404).json({
          success: false,
          message: "Leave request not found",
        } as APIResponse);
        return;
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.PENDING) {
        res.status(400).json({
          success: false,
          message: `Cannot cancel leave request with status: ${leaveRequest.status}`,
        } as APIResponse);
        return;
      }

      await leaveRequest.cancel();

      logger.info(`Leave request cancelled: ${leaveRequest.id}`, {
        leaveRequestId: leaveRequest.id,
        studentId: student.id,
      });

      res.status(200).json({
        success: true,
        message: "Leave request cancelled successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            status: leaveRequest.status,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Cancel leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to cancel leave request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get leave request statistics for school
   * @route GET /api/v1/school/leave-requests/statistics
   * @access Private (School Admin)
   */
  async getLeaveRequestStatistics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      const stats = await LeaveRequest.aggregate([
        {
          $match: { schoolId: new mongoose.Types.ObjectId(schoolId) },
        },
        {
          $group: {
            _id: "$status",
            count: { $sum: 1 },
          },
        },
      ]);

      const leaveTypeStats = await LeaveRequest.aggregate([
        {
          $match: { schoolId: new mongoose.Types.ObjectId(schoolId) },
        },
        {
          $group: {
            _id: "$leaveType",
            count: { $sum: 1 },
          },
        },
      ]);

      const result: any = {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
        byType: {},
      };

      stats.forEach((stat: any) => {
        result.total += stat.count;
        result[stat._id] = stat.count;
      });

      leaveTypeStats.forEach((stat: any) => {
        result.byType[stat._id] = stat.count;
      });

      res.status(200).json({
        success: true,
        message: "Leave request statistics retrieved successfully",
        data: {
          statistics: result,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get leave request statistics error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve statistics",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get leave requests for teacher's students
   * @route GET /api/v1/teacher/leave-requests
   * @access Private (Teacher)
   */
  async getTeacherLeaveRequests(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { classId, status, page = 1, limit = 20 } = req.query;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
        } as APIResponse);
        return;
      }

      // Get teacher's classes
      const teacherClasses = await Class.find({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        teacherIds: teacher._id,
      }).select("_id studentIds");

      const classIds = teacherClasses.map(c => c._id);
      const studentIds = new Set<string>();

      // Collect all student IDs from teacher's classes
      for (const classItem of teacherClasses) {
        if (classItem.studentIds) {
          classItem.studentIds.forEach((id: mongoose.Types.ObjectId) => {
            studentIds.add(id.toString());
          });
        }
      }

      if (classIds.length === 0 || studentIds.size === 0) {
        res.status(200).json({
          success: true,
          message: "No students found in your classes",
          data: {
            leaveRequests: [],
            pagination: {
              page: 1,
              limit: 20,
              total: 0,
              pages: 0,
            },
          },
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        studentId: {
          $in: [...studentIds].map(id => new mongoose.Types.ObjectId(id)),
        },
      };

      if (status) {
        filterQuery.status = status;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const leaveRequests = await LeaveRequest.find({ ...filterQuery })
        .populate("student", "fullName studentId profilePicture")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email")
        .populate("appeal.reviewedBy", "fullName email")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await LeaveRequest.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Leave requests retrieved successfully",
        data: {
          leaveRequests: leaveRequests.map(lr =>
            formatLeaveRequestDocument(lr),
          ),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get teacher leave requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve leave requests",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const leaveController = new LeaveController();
